'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Send, CheckCircle } from 'lucide-react';

type ContactForm = {
  name: string;
  email: string;
  apartment: string;
  message: string;
};

export default function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactForm>();

  // BACKEND INTEGRATION: POST /api/contact — send form data to host email
  const onSubmit = async (_data: ContactForm) => {
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1200));
    setLoading(false);
    setSubmitted(true);
    reset();
  };

  return (
    <section id="contact" className="bg-muted/40 py-16">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-xs font-700 uppercase tracking-widest text-primary mb-2">Get in Touch</p>
            <h2 className="text-hero-md font-800 text-foreground">Ask Maria Anything</h2>
            <p className="text-muted-foreground mt-2 text-sm">
              Questions about an apartment, availability, or a special request? Maria responds within 2 hours.
            </p>
          </div>

          {submitted ? (
            <div className="card-base p-10 text-center animate-fade-in">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h3 className="font-700 text-lg text-foreground mb-2">Message Sent!</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Maria will get back to you within 2 hours. Check your inbox for a confirmation.
              </p>
              <button onClick={() => setSubmitted(false)} className="btn-outline text-sm py-2.5 px-5">
                Send Another Message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="card-base p-6 lg:p-8 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-700 uppercase tracking-wider text-muted-foreground mb-1.5">
                    Your Name <span className="text-primary">*</span>
                  </label>
                  <input
                    {...register('name', { required: 'Name is required' })}
                    placeholder="Sophie Marchand"
                    className="input-base"
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
                </div>
                <div>
                  <label className="block text-xs font-700 uppercase tracking-wider text-muted-foreground mb-1.5">
                    Email Address <span className="text-primary">*</span>
                  </label>
                  <input
                    {...register('email', {
                      required: 'Email is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                    })}
                    type="email"
                    placeholder="sophie@example.com"
                    className="input-base"
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>}
                </div>
              </div>

              <div>
                <label className="block text-xs font-700 uppercase tracking-wider text-muted-foreground mb-1.5">
                  Apartment Interest
                </label>
                <select {...register('apartment')} className="input-base">
                  <option value="">Any / Not sure yet</option>
                  <option value="apt-001">Sunset Terrace Suite</option>
                  <option value="apt-002">Ocean Breeze Loft</option>
                  <option value="apt-003">Garden Villa Escape</option>
                  <option value="apt-004">Rooftop Haven</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-700 uppercase tracking-wider text-muted-foreground mb-1.5">
                  Message <span className="text-primary">*</span>
                </label>
                <textarea
                  {...register('message', { required: 'Please write a message', minLength: { value: 20, message: 'Message must be at least 20 characters' } })}
                  rows={4}
                  placeholder="Hi Maria, I'm interested in booking the Sunset Terrace Suite for the first week of July..."
                  className="input-base resize-none"
                />
                {errors.message && <p className="mt-1 text-xs text-red-500">{errors.message.message}</p>}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ minHeight: '48px' }}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Sending...
                  </span>
                ) : (
                  <>
                    <Send size={16} />
                    Send Message to Maria
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}